import React from 'react'
import { Link } from 'react-router-dom'
import partition from 'lodash/fp/partition'
import ListSubheader from '@mui/material/ListSubheader'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import Collapse from '@mui/material/Collapse'
import HomeIcon from '@mui/icons-material/Home'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {
  GetReportDetailsQuery,
  GetReportFavoriteDetailsQuery,
  ReportSection,
} from 'gqlTypes'
import RemoveIcon from '@mui/icons-material/Remove'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CommonSidebar from './CommonSidebar'
import { HEADER_HEIGHT } from './ReportHeader'
import Typography from '@mui/material/Typography'

interface Props {
  report:
    | NonNullable<GetReportDetailsQuery['report']>
    | NonNullable<GetReportFavoriteDetailsQuery['reportWithFavorites']>
  sectionId?: string
  favorites?: boolean
}
const ReportContents: React.FC<Props> = ({
  report,
  sectionId,
  favorites = false,
}) => {
  const [rootSections, childSections] = partition(
    (s) => s.parentReportSectionId == null,
    report.sections
  )

  const getRootSectionPath = (
    section: ReportSection,
    sectionChildren: ReportSection[]
  ) => {
    let id = section.id
    if (
      favorites &&
      sectionChildren.length &&
      !section.reportFavorite &&
      !section.hasElementFavorites
    ) {
      id = sectionChildren[0]?.id
    }
    return `/${favorites ? 'report_bookmarks' : 'reports'}/${
      report.id
    }/sections/${id}`
  }

  return (
    <CommonSidebar>
      <List
        sx={{ width: '100%', paddingTop: `${HEADER_HEIGHT}px` }}
        subheader={
          <ListSubheader component="div" sx={{ color: 'primary.main' }}>
            <Typography
              component="h3"
              variant="overline"
              sx={{ color: 'primary.main', py: 2 }}
            >{`${favorites ? 'Bookmarked Sections' : 'Sections'} (${
              rootSections.length + childSections.length
            })`}</Typography>
          </ListSubheader>
        }
      >
        <Divider />
        <ListItemButton
          component={Link}
          to={`/reports/${report.id}/`}
          selected={!sectionId}
        >
          <ListItemIcon>
            <HomeIcon />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="h6" component="h5">
                Report Overview
              </Typography>
            }
          />
        </ListItemButton>
        <Divider />
        {rootSections.map((section) => {
          const sectionChildren = childSections.filter(
            (cs) => cs.parentReportSectionId === section.id
          )
          const sectionIsSelected =
            section.id === sectionId ||
            sectionChildren.some((s) => s.id === sectionId)
          return (
            <React.Fragment key={section.id}>
              <ListItemButton
                component={Link}
                to={getRootSectionPath(section, sectionChildren)}
                selected={sectionIsSelected}
              >
                <ListItemText primary={section.title} sx={{ pl: 3 }} />
                {!!sectionChildren.length && (
                  <ChevronRightIcon
                    sx={[
                      {
                        transform: 'rotate(0deg)',
                        transition: (theme) =>
                          theme.transitions.create('all', {
                            duration: theme.transitions.duration.shortest,
                          }),
                      },
                      sectionIsSelected && {
                        transform: 'rotate(90deg)',
                      },
                    ]}
                  />
                )}
              </ListItemButton>
              <Collapse in={sectionIsSelected}>
                <List component="div" disablePadding>
                  {sectionChildren.map((cs) => (
                    <ListItemButton
                      key={cs.id}
                      selected={cs.id === sectionId}
                      sx={{
                        pl: 4.8,
                        '&.Mui-selected': {
                          backgroundColor: '#F7CD6D',
                          '&:hover': {
                            backgroundColor: '#F5C75B',
                          },
                          '& span': {
                            fontWeight: 'normal',
                          },
                        },
                      }}
                      component={Link}
                      to={`/${favorites ? 'report_bookmarks' : 'reports'}/${
                        report.id
                      }/sections/${cs.id}`}
                    >
                      <ListItemIcon>
                        {cs.id === sectionId ? (
                          <PlayArrowIcon />
                        ) : (
                          <RemoveIcon sx={{ color: '#ccc' }} />
                        )}
                      </ListItemIcon>
                      <ListItemText primary={cs.title} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
              <Divider />
            </React.Fragment>
          )
        })}
      </List>
    </CommonSidebar>
  )
}

export default ReportContents
